import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import EditMenuButton from '../../Modal/EditMenuButton';
import ConfirmDeleteDialog from '../../Modal/ConfirmDelete';
import { Box, Button, FormControl } from '@mui/material';
import ModifyRiskSourceModal from './ModifyRiskSourceModal';
import RiskSourceApiService from '../../../api/RiskSourceApiService';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../Common/SnackbarProvider';

const RiskSources = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const [riskSources, setRiskSources] = useState([]);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedRiskSource, setSelectedRiskSource] = useState();

    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: t('common.id'),
            flex: 0.5,
        },
        {
            field: 'name',
            headerName: t('fieldName.riskSource'),
            flex: 2,
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setSelectedRiskSource}
                    onEditFunc={() => {
                        setShowEditDialog(true);
                        setSelectedRiskSource(params.row)
                    }}
                />;
            },
        }
    ], [t]);

    const riskSourceApiService = new RiskSourceApiService();

    async function getRiskSources () {
        const riskSources = await riskSourceApiService.getRiskSources();
        setRiskSources(riskSources.data ?? []);
    }

    async function onDeleteClick(value) {
        if (!value) {
            setShowDeleteDialog(false);
            return;
        }

        if (selectedRiskSource) {
            const deletedRiskSource = await riskSourceApiService.deleteRiskSource(selectedRiskSource.id);
            if (deletedRiskSource && deletedRiskSource.success) {
                snackbar.warning(t('riskSourcesPage.toast.deleted'));
                await getRiskSources();
            }
        }

        setShowDeleteDialog(false);
    }

    async function handleEditClose() {
        setShowEditDialog(false);
        setSelectedRiskSource(null);
        await getRiskSources();
    }

    useEffect(() => {
        void getRiskSources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return(
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Box sx={{ mb: 2 }}>
                <h2>{t('riskSourcesPage.title')}</h2>
            </Box>
            <Box sx={{ mb: 2 }}>
                <div style={{ display: 'inline-block', justifyContent: 'space-between' }}>
                    <FormControl sx={{ m: 1, minWidth: 120, float: 'left' }}>
                        <Button size={'large'} variant="contained" onClick={() => setShowEditDialog(true) }>{t('riskSourcesPage.createButton')}</Button>
                    </FormControl>
                </div>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    columns={columns}
                    rows={riskSources}
                    getRowId={(row) => row.id}
                    getRowHeight={() => 'auto'}
                />
            </Box>
            <ConfirmDeleteDialog
                onClose={onDeleteClick}
                open={showDeleteDialog}
            />
            <ModifyRiskSourceModal
                open={showEditDialog}
                onClose={handleEditClose}
                selectedRiskSource={selectedRiskSource}
                riskSourceApiService={riskSourceApiService}
                onCancel={handleEditClose}
            />
        </Box>
    );
}

export default RiskSources;
